import { useEffect, useRef, useState } from "react";
import { HOMEWORK_MAX_EXERCISES } from "@edu/shared";
import type { TopicPicks } from "../../exercises/topics/use-topic-picks.ts";
import { SHEET_ORDER_NOTE, fitUrlPicks, urlPicksNote, urlPicksTitle } from "./url-picks.ts";

/**
 * Второй вход в форму выдачи: состав заданий пришёл адресом (6.3 и 7.3
 * docs/student-errors-design.md). Панель «Где дыры» в карточке ученика собирает
 * состав диагностического листа и уводит сюда ссылкой.
 *
 * Отдельным хуком от формы по той же причине, что и ход раздачи: форма отвечает
 * на «кому и когда», а здесь живёт «что нам продиктовали адресом и что от него
 * уцелело». Сам разбор при этом ещё дальше, в чистом `./url-picks.ts` с тестом:
 * тестов на компоненты в проекте нет, а границы мягкого разбора проверить надо.
 *
 * ⚠️ Строки собирает `topics.restore`, написанная ради продолжения брошенной
 * раздачи. Второго кода для той же задачи не заводится: два способа положить
 * строки в форму однажды разъедутся в правилах потолка.
 */

export interface UrlPicksEntry {
  /** Что из адреса не уцелело, одной строкой. `null` значит «всё на месте». */
  readonly lost: string | null;
  /**
   * Подпись предпросмотра про порядок строк. `null`, когда состав набран
   * руками: там порядок свой, и объяснять нечего.
   */
  readonly orderNote: string | null;
  /**
   * Забыть адрес. Зовётся при восстановлении брошенной раздачи: продолжение
   * сильнее ссылки, иначе приехавший позже справочник затёр бы восстановленные
   * строки составом из адреса.
   */
  readonly drop: () => void;
}

export function useUrlPicks(args: {
  /** Значение `?picks=` как есть. Разбирается мягко, отказа не даёт никогда. */
  raw: string | null;
  topics: TopicPicks;
  /** Название работы по составу. Зовётся один раз и только с непустым именем. */
  onTitle: (title: string) => void;
}): UrlPicksEntry {
  const [lost, setLost] = useState<string | null>(null);
  /**
   * ⚠️ Состав кладётся в строки РОВНО ОДИН РАЗ. Иначе правка руками
   * откатывалась бы к написанному в ссылке, а обратимость формы это требование
   * 7.3 проекта: предзаполнение это предложение, а не решение за человека.
   */
  const applied = useRef(args.raw === null);

  const { raw, topics, onTitle } = args;
  const known = topics.topics;

  useEffect(() => {
    // Ждём справочник: до его приезда неизвестен ни один потолок, и подрезать
    // количество было бы нечем
    if (applied.current || known === null) return;
    applied.current = true;
    const fit = fitUrlPicks(raw, known, HOMEWORK_MAX_EXERCISES);
    topics.restore(fit.picks);
    setLost(urlPicksNote(fit));
    const title = urlPicksTitle(fit.picks, known);
    if (title !== null) onTitle(title);
    // В зависимостях приезд справочника, а не весь `topics`: у хука новая
    // ссылка на каждый рендер, и целиком он гонял бы эффект вхолостую
  }, [known, raw]);

  /**
   * ⚠️ Подпись СВОЯ и приходить из адреса не может, это снято на ревью
   * 22.08.2026. Был параметр `?note=`, читавший её из ссылки «на будущее, когда
   * панель начнёт класть серверный текст». Убран по двум причинам.
   *
   * Первая: произвольный текст из адреса, напечатанный в нашей рамке, это
   * готовая приманка. Ссылку присылают преподавателю в мессенджер, и наш экран
   * показал бы чужую фразу как собственную подпись. Сегодня выгоды от параметра
   * ноль, потому что его не кладёт никто, а поверхность уже есть.
   *
   * Вторая: адрес это неверный канал и на будущее. Когда серверная подпись
   * понадобится (у второго предмета она другая), форма возьмёт её у той же
   * ручки состава листа, тему для которой она и так знает, а не из ссылки,
   * которую мог собрать кто угодно.
   */
  return {
    lost,
    orderNote: raw === null ? null : SHEET_ORDER_NOTE,
    drop: () => {
      applied.current = true;
      setLost(null);
    },
  };
}
